import {
    CELL_FORMAT_CUSTOM,
    COHORT_METRIC_TYPE,
    DEFAULT_OPERATOR,
    DEFAULT_PERIOD,
    DEFAULT_RESULT_OPERATOR,
    METRIC_NAME_MAX_LENGTH,
    NEW_METRIC_ID,
    OPERATOR_TYPES,
    REF_TABLE_PREFIX,
    getDefaultCustomMetric,
    getDefaultResult,
} from './consts';
import { slugify } from './slugify';

let idCounter = 0;
export const generateId = () => {
    idCounter += 1;
    return `custom-metric-${idCounter}`;
};

export const normalizeName = displayName => slugify(displayName).replace(/-/g, '_');

export const getFormatJSON = metric => {
    const isCustomFormat = metric.field_type?.key === CELL_FORMAT_CUSTOM;

    if (isCustomFormat) {
        return JSON.stringify(metric.custom_display_format || {});
    }

    if (!metric.field_type) {
        return '';
    }

    const { key, ...format } = metric.field_type;
    return JSON.stringify(format);
};

const getOperatorType = (operatorName, dimension) => {
    if (['gted', 'lted'].includes(operatorName)) {
        return OPERATOR_TYPES.DATE;
    }

    return dimension.raw_values ? OPERATOR_TYPES.LIST : OPERATOR_TYPES.TEXT;
};

export const expandDimensions = dimensions =>
    dimensions.map(dimension => ({
        ...dimension,
        operators: (dimension.operators || []).map(operator => ({
            ...operator,
            type: getOperatorType(operator.name, dimension),
        })),
    }));

export const expandDisplayFormats = displayFormats =>
    Object.entries(displayFormats).map(([key, format]) => ({ ...(format || {}), key }));

const wrapValueInNames = (value, rawValues) => {
    if (!rawValues) {
        return { display_name: value };
    }

    return rawValues.find(rawValue => rawValue.name === value) || { name: value, display_name: value };
};

const expandExpression = (wireExpression, dimensions) => {
    const [dimensionName, operatorName, wireValue] = wireExpression;
    const dimension = dimensions.find(({ name }) => name === dimensionName) || {};
    const operator = (dimension.operators || []).find(({ name }) => name === operatorName) || { ...DEFAULT_OPERATOR };

    const isValueList = [OPERATOR_TYPES.LIST, OPERATOR_TYPES.TEXT].includes(operator.type);

    return {
        dimension,
        operator,
        value: isValueList ? (wireValue || []).map(value => wrapValueInNames(value, dimension.raw_values)) : wireValue,
    };
};

const expandResultExpression = (wireResult, isRefTable, { allMetrics, refTables, resultOperators }) => {
    const [metricName, operatorName, wireValue] = wireResult;
    const value = isRefTable
        ? refTables.find(({ name }) => name === wireValue)
        : allMetrics.find(({ name }) => name === wireValue);

    const result = {
        metric: allMetrics.find(({ name }) => name === metricName) || getDefaultResult().metric,
        operator: resultOperators.find(({ name }) => name === operatorName) || { ...DEFAULT_RESULT_OPERATOR },
        value: value || wireValue,
    };

    return {
        ...result,
        isRefTable: !!isRefTable,
        showOpValue: !!(result.operator.name && (result.value || result.value === 0)),
    };
};

const expandRequiredPeriods = metric => {
    const periods = { ...(metric.periods || {}) };

    metric.rules.forEach(rule => {
        ['metric', 'value'].forEach(fieldName => {
            const field = rule.result[fieldName];
            if (field && field.metric_type === COHORT_METRIC_TYPE && !(field.name in periods)) {
                periods[field.name] = DEFAULT_PERIOD.period_id;
            }
        });
    });

    return periods;
};

export const expandCustomMetric = (wireMetric, context) => {
    const { dimensions, displayFormatsList, cohortPeriods } = context;

    const metric = { ...getDefaultCustomMetric(), ...wireMetric };

    metric.rules = (wireMetric.rules || []).map(rule => ({
        id: generateId(),
        expression: (rule.expression[0] || []).map(expression => expandExpression(expression, dimensions)),
        result: expandResultExpression(rule.result, rule.is_ref_table_rule, context),
    }));

    metric.id = generateId();
    metric.oldName = wireMetric.name;
    metric.default_result = expandResultExpression(
        wireMetric.default_result,
        typeof wireMetric.default_result[2] === 'string' && wireMetric.default_result[2].startsWith(REF_TABLE_PREFIX),
        context
    );
    metric.field_type = displayFormatsList.find(({ key }) => key === wireMetric.field_type) || null;
    metric.json = getFormatJSON(metric);

    const periods = expandRequiredPeriods(metric);
    metric.periods = Object.fromEntries(
        Object.entries(periods).map(([metricName, periodId]) => [
            metricName,
            cohortPeriods.find(period => `${period.period_id}` === `${periodId}`) || { ...DEFAULT_PERIOD },
        ])
    );

    return metric;
};

const unwrapValueName = value => (value.name !== undefined ? value.name : value.display_name);

const parseExpressionValue = expressionValue => {
    if (Array.isArray(expressionValue)) {
        return expressionValue.map(unwrapValueName);
    }

    return expressionValue;
};

const serializeResult = result => {
    const { value } = result;
    const isObjectValue = typeof value === 'object' && value !== null;
    const isEmptyValue = !value && value !== 0;
    let serializedValue = null;
    if (isObjectValue) {
        serializedValue = value.name;
    } else if (!isEmptyValue) {
        serializedValue = Number(value);
    }

    return [result.metric ? result.metric.name : '', result.operator.name || null, serializedValue];
};

export const serializeCustomMetric = metric => {
    const serialized = {
        name: metric.name,
        display_name: metric.display_name,
        visible: metric.visible,
        is_default: metric.is_default,
        is_restricted: metric.is_restricted,
        should_appear_as_custom_event: metric.should_appear_as_custom_event,
        field_type: metric.field_type?.key,
        tooltip: metric.tooltip,
        custom_display_format: metric.field_type?.key === CELL_FORMAT_CUSTOM ? JSON.parse(metric.json) : {},
        rules: metric.rules.map(rule => ({
            expression: [
                rule.expression.map(expression => [
                    expression.dimension ? expression.dimension.name : '',
                    expression.operator.name,
                    parseExpressionValue(expression.value),
                ]),
            ],
            result: serializeResult(rule.result),
        })),
        default_result: serializeResult(metric.default_result),
    };

    const periods = Object.fromEntries(
        Object.entries(metric.periods || {})
            .map(([metricName, period]) => [metricName, `${period.period_id}`])
            .filter(([, periodId]) => periodId !== `${DEFAULT_PERIOD.period_id}`)
    );

    if (Object.keys(periods).length) {
        serialized.periods = periods;
    }

    return serialized;
};

const isEmptyExpressionValue = value => !value || (Array.isArray(value) && !value.length);

const validateResult = (result, metricName) => {
    const resultMetricName = result.metric?.name;
    const hasOperator = !!result.operator.name;
    const isEmptyValue = !result.value && result.value !== 0;
    const hasValue = (typeof result.value === 'object' && result.value !== null) || !isEmptyValue;

    return {
        metric: !resultMetricName,
        metricCollision: !!resultMetricName && resultMetricName === metricName,
        opValue: (hasOperator && !hasValue) || (!hasOperator && hasValue),
    };
};

export const validateCustomMetric = metric => {
    const errors = {
        names:
            !metric.name ||
            !metric.display_name ||
            metric.name.length > METRIC_NAME_MAX_LENGTH ||
            metric.display_name.length > METRIC_NAME_MAX_LENGTH,
        fieldType: !metric.field_type || (!metric.field_type.type && metric.field_type.key !== CELL_FORMAT_CUSTOM),
        formatJson: false,
        rules: [],
        defaultResult: validateResult(metric.default_result, metric.name),
    };

    if (metric.field_type?.key === CELL_FORMAT_CUSTOM) {
        errors.fieldType = false;
        try {
            JSON.parse(metric.json);
        } catch (e) {
            errors.formatJson = true;
        }
    }

    errors.rules = metric.rules.map(rule => {
        const ruleErrors = {
            result: validateResult(rule.result, metric.name),
            expressions: [],
        };

        const [firstExpression] = rule.expression;
        const isEmptyRefTableExpression =
            rule.result.isRefTable &&
            rule.expression.length === 1 &&
            !firstExpression.dimension.name &&
            isEmptyExpressionValue(firstExpression.value);

        ruleErrors.expressions = rule.expression.map(expression => {
            if (isEmptyRefTableExpression) {
                return { dimension: false, operator: false, value: false };
            }

            return {
                dimension: !expression.dimension.name,
                operator: !expression.operator.name,
                value: isEmptyExpressionValue(expression.value),
            };
        });

        return ruleErrors;
    });

    const hasResultErrors = resultErrors => resultErrors.metric || resultErrors.metricCollision || resultErrors.opValue;

    const isValid =
        !errors.names &&
        !errors.fieldType &&
        !errors.formatJson &&
        !hasResultErrors(errors.defaultResult) &&
        errors.rules.every(
            ruleErrors =>
                !hasResultErrors(ruleErrors.result) &&
                ruleErrors.expressions.every(
                    expressionErrors =>
                        !expressionErrors.dimension && !expressionErrors.operator && !expressionErrors.value
                )
        );

    return { errors, isValid };
};

export const isNewMetric = metric => metric.id === NEW_METRIC_ID || !metric.oldName;

export const processPageData = ({
    dimensions,
    expressionOperators,
    displayFormats,
    customMetrics,
    allMetrics: allMetricsByType,
    refTables,
    periods,
}) => {
    const allMetrics = Object.entries(allMetricsByType).flatMap(([metricType, metrics]) =>
        metrics.map(metric => ({ ...metric, metric_type: metricType }))
    );

    const visibleMetrics = allMetrics.filter(({ visible }) => !!visible);
    const taggedRefTables = refTables.map(refTable => ({ ...refTable, isRefTable: true }));
    const resultOperators = [{ ...DEFAULT_RESULT_OPERATOR }, ...expressionOperators];
    const context = {
        dimensions: expandDimensions(dimensions),
        resultOperators,
        displayFormatsList: expandDisplayFormats(displayFormats),
        allMetrics,
        visibleMetrics,
        refTables: taggedRefTables,
        allResultValues: [...visibleMetrics, ...taggedRefTables],
        cohortPeriods: [{ ...DEFAULT_PERIOD }, ...periods],
    };

    return {
        ...context,
        customMetrics: customMetrics.map(metric => expandCustomMetric(metric, context)),
    };
};
